import { showToast } from '@bp/ui-components';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { eventJobRunningStore } from '../stores/eventJobRunningStore';
import { apiHelper } from './apiHelper';

// Keine Sperre mehr vor dem Anstoßen: Das Backend meldet die betroffenen Tage, der Event-Worker arbeitet sie ab, auch
// wenn schon ein Abgleich läuft (planung-backend, docs/plans/2026-11-03-event-sync-target-state-plan.md).

/**
 * Nach dem Anlegen, Ändern oder Löschen von Ferien stößt das Backend selbst den Abgleich der Termine an
 * (`change-holiday.workflow.ts`), aber nur für Tage ab heute. Deshalb nur dann auf "läuft" schalten, wenn die alten
 * oder neuen Ferien bis heute oder später reichen; sonst stünde der Hinweis bis zur TTL grundlos da.
 */
export const markEventJobStartedForHolidays = (...holidays: ({ end: string | Date } | null | undefined)[]) => {
  if (holidays.some((holiday) => holiday && !dayjs(holiday.end).isBefore(dayjs(), 'day'))) {
    eventJobRunningStore.markStarted();
  }
};

export const deleteEvents = async ({
  organizationUuid,
  schoolYearUuid,
}: {
  organizationUuid: string;
  schoolYearUuid: string;
}) => {
  await apiHelper<void>({
    method: 'DELETE',
    path: `/events/${organizationUuid}/${schoolYearUuid}`,
  });
};

export const createEvents = async ({
  organizationUuid,
  schoolYearUuid,
}: {
  organizationUuid: string;
  schoolYearUuid: string;
}) => {
  eventJobRunningStore.markStarted();
  await apiHelper<void>({
    method: 'POST',
    path: `/events/${organizationUuid}/${schoolYearUuid}`,
  });
};

/**
 * Stösst die Event-Erzeugung für einen Datumsbereich an. Das Backend verwirft den Auftrag,
 * wenn der Bereich nicht vollständig im übergebenen Schuljahr liegt – dann wird weder ein Job
 * eingereiht noch das "läuft"-Flag gesetzt.
 *
 * @returns ob der Job tatsächlich eingereiht wurde
 */
export const createEventsForDateRange = async ({
  organizationUuid,
  schoolYearUuid,
  from,
  to,
  epochSubjectContainerUuid,
}: {
  organizationUuid: string;
  schoolYearUuid: string;
  from: string;
  to: string;
  epochSubjectContainerUuid?: string;
}): Promise<boolean> => {
  const result = await apiHelper<{ success?: boolean } | undefined>({
    method: 'POST',
    path: `/events/${organizationUuid}/date-range`,
    type: 'json',
    suppressErrorToast: true,
    data: JSON.stringify({ schoolYearUuid, from, to, epochSubjectContainerUuid }),
  });

  if (!result?.success) {
    showToast(t('synchronizations.startFailed'), { type: 'error', autoClose: 5000 });
    return false;
  }

  // Erst jetzt optimistisch auf "läuft" schalten: vorher wäre das Banner auch dann bis zur
  // TTL sichtbar, wenn das Backend den Auftrag abgelehnt hat.
  eventJobRunningStore.markStarted();
  return true;
};

export const createEventsForVersion = async ({
  organizationUuid,
  timetableVersionUuid,
}: {
  organizationUuid: string;
  timetableVersionUuid: string;
}) => {
  eventJobRunningStore.markStarted();
  await apiHelper<void>({
    method: 'POST',
    path: `/events/${organizationUuid}/timetable-version/${timetableVersionUuid}`,
  });
};
